import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedModule } from '@app/shared/shared.module';
import { OrderModule } from '../order/order.module';
import { UIRouterModule } from '@uirouter/angular';
import { RedirectToResult, Transition } from '@uirouter/angular';
import { WebshopComponent } from './webshop.component';
import { WebshopBillingComponent } from './billing/webshop-billing.component';
import { WebshopSupplierComponent } from './supplier/webshop-supplier.component';
import { WebshopInfoComponent } from './info/webshop-info.component';
import { WebshopSetupComponent } from './setup/webshop-setup.component';
import { WebshopGuideComponent } from './guide/webshop-guide.component';
import { WebshopAccessSettingsComponent } from './access-settings/webshop-access-settings.component';
import { WebshopCatalogSelectionDialogComponent } from './catalog-selection/webshop-catalog-selection-dialog.component';
import { WebshopRoleAddComponent } from './roles/webshop-role-add.component';
import { EmailTesterPopupComponent } from './access-settings/email-tester-popup.component';
import { SSOSectionFieldsComponent } from './access-settings/sso-section-fields.component';
import { UserApprovalRadioFieldsComponent } from './access-settings/user-approval-radio-fields.component';
import { UserManualApprovalFieldComponent } from './access-settings/user-manual-approval-field.component';
import { UserAutoApprovalFieldComponent } from './access-settings/user-auto-approval-field.component';
import { WebshopBillingDataService } from './billing/webshop-billing-data.service';
import { STATE_ACCESSMANAGEMENT, STATE_BROWSELIST, STATE_GUIDEMANAGEMENT, STATE_WEBSHOP, STATE_WEBSHOPB2BSETTINGS, STATE_WEBSHOPBILLING, STATE_WEBSHOPINFO, STATE_WEBSHOPMANAGEMENT, STATE_WEBSHOPROLES, STATE_WEBSHOPSETUP, STATE_WEBSHOPSUPPLIER } from '@app/shared/app-states';
import { WebshopSupplierDataService } from './supplier/webshop-supplier-data.service';
import { SEC_WS_ACCESS, SEC_WS_B2BSETTINGS, SEC_WS_BILLING, SEC_WS_CATALOGSYNC, SEC_WS_INFO, SEC_WS_SUPPLIER } from '@app/shared/services/auth/security-objects';
import { WebshopInfoService } from './info/webshop-info.service';
import { WebshopInfoDataService } from './info/webshop-info-data.service';
import { OrderCatalogService } from '../order/catalog/order-catalog.service';
import { WebshopGuideDataService } from './guide/webshop-guide-data.service';
import { CatalogGuideDataService } from '../order/catalog/guide/catalog-guide-data.service';
import { WebshopCatalogSelectionDataService } from './catalog-selection/webshop-catalog-selection-data.service';
import { WebshopAccessSettingsDataService } from './access-settings/webshop-access-settings-data.service';
import { WebshopRoleDataService } from './roles/webshop-role-data.service';
import { GlobalProductModule } from '../globalproduct/global-product.module';
import { WebshopService } from './webshop.service';
import { UiViewComponent } from '@app/shared/ui-router/ui-view.component';
import { OrderCatalogDataService } from '../order/catalog/order-catalog-data.service';
import { WebshopB2BSettingsComponent } from './b2b-settings/webshop-b2b-settings.component';
import { WebshopB2BSettingsDataService } from './b2b-settings/webshop-b2b-settings-data.service';
import { TranslationCategory } from '@app/shared/services/ga-state.service';
import { GaStateDeclaration } from '@type_definitions/datatypes';
import { WebshopWrapperComponent } from './webshop-wrapper.component';
import { TRANSLATION_READY_RESOLVER } from '@app/app-routes.config';

const webshopTitle:string = 'GA.GENERAL.WEBSHOP';
const modules:TranslationCategory[] = [ TranslationCategory.WEBSHOP ]; // load webshop translation keys

const roleUrlReplace = (trans:Transition):RedirectToResult => {
  const webshopId:string = trans.params().webshopId;
  return {
    state: STATE_BROWSELIST,
    params: {
      listCode: 'WEBSHOP_ROLES',
      filters: `Desprnm%09${webshopId}`,
      autoload: true
    }
  };
};

export const states:GaStateDeclaration[] = [
  {
    name: STATE_WEBSHOP,
    abstract: true,
    url: '/webshop/{webshopId}',
    component: WebshopComponent,
    resolve: [
      {
        token: 'webshopId',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => Number(trans.params().webshopId)
      },
      {
        token: 'stateParams',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => trans.params()
      }
    ]
  },
  {
    name: STATE_WEBSHOPMANAGEMENT,
    url: '/management',
    abstract: true,
    component: UiViewComponent
  },
  {
    name: STATE_WEBSHOPBILLING,
    url: '/billing',
    component: WebshopBillingComponent,
    resolve: [
      {
        token: 'menu',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => trans.params().menu
      },
      TRANSLATION_READY_RESOLVER
    ],
    bindings: {
      menu: 'menu'
    },
    data: {
      viewCode: SEC_WS_BILLING,
      viewTitle: [
        webshopTitle,
        'WEBSHOP.BILLING.HEADING.TITLE'
      ],
      modules
    }
  },
  {
    name: STATE_WEBSHOPSUPPLIER,
    url: '/supplier',
    component: WebshopSupplierComponent,
    resolve: [TRANSLATION_READY_RESOLVER],
    data: {
      viewCode: SEC_WS_SUPPLIER,
      viewTitle: [
        webshopTitle,
        'WEBSHOP.SUPPLIER.HEADING.TITLE'
      ],
      modules
    }
  },
  {
    name: STATE_WEBSHOPB2BSETTINGS,
    url: '/b2b-settings',
    component: WebshopB2BSettingsComponent,
    resolve: [TRANSLATION_READY_RESOLVER],
    data: {
      viewCode: SEC_WS_B2BSETTINGS,
      viewTitle: [
        webshopTitle,
        'WEBSHOP.B2B_SETTINGS.HEADING.TITLE'
      ],
      modules
    }
  },
  {
    name: STATE_WEBSHOPINFO,
    url: '/webshop-info',
    component: WebshopInfoComponent,
    resolve: [TRANSLATION_READY_RESOLVER],
    data: {
      viewCode: SEC_WS_INFO,
      viewTitle: [
        webshopTitle,
        'WEBSHOP.WEBSHOP_INFO.HEADING.TITLE'
      ],
      modules
    }
  },
  {
    name: STATE_WEBSHOPSETUP,
    url: '',
    component: WebshopSetupComponent,
    resolve: [TRANSLATION_READY_RESOLVER],
    data: {
      viewTitle: 'Webshop Setup',
      validate: [ 'SAVE_WEBSHOP' ],
      modules
    }
  },
  {
    name: STATE_GUIDEMANAGEMENT,
    url: '/guide',
    component: WebshopGuideComponent,
    resolve: [TRANSLATION_READY_RESOLVER],
    data: {
      viewCode: SEC_WS_CATALOGSYNC,
      viewTitle: [
        webshopTitle,
        'WEBSHOP.TABS.GUIDE'
      ],
      modules
    }
  },
  {
    name: STATE_ACCESSMANAGEMENT,
    url: '/access-settings',
    component: WebshopAccessSettingsComponent,
    resolve: [TRANSLATION_READY_RESOLVER],
    data: {
      viewCode: SEC_WS_ACCESS,
      viewTitle: [
        webshopTitle,
        'WEBSHOP.ACCESS_SETTINGS.HEADING.TITLE'
      ],
      modules
    }
  },
  // redirect to webshop browselist
  {
    name: STATE_WEBSHOPROLES,
    url: '/roles',
    redirectTo: roleUrlReplace
  }
];


@NgModule({
  declarations: [
    WebshopComponent,
    WebshopWrapperComponent,
    WebshopBillingComponent,
    WebshopSupplierComponent,
    WebshopInfoComponent,
    WebshopSetupComponent,
    WebshopGuideComponent,
    WebshopCatalogSelectionDialogComponent,
    WebshopAccessSettingsComponent,
    WebshopB2BSettingsComponent,

    WebshopRoleAddComponent,
    EmailTesterPopupComponent,
    UserApprovalRadioFieldsComponent,
    UserManualApprovalFieldComponent,
    UserAutoApprovalFieldComponent,
    SSOSectionFieldsComponent
  ],
  imports: [
    UIRouterModule.forChild({ states }),
    CommonModule,
    SharedModule,
    OrderModule,
    GlobalProductModule
  ],
  providers: [
    WebshopBillingDataService,
    WebshopSupplierDataService,
    WebshopInfoService,
    WebshopInfoDataService,
    WebshopGuideDataService,
    WebshopCatalogSelectionDataService,
    WebshopAccessSettingsDataService,
    CatalogGuideDataService,
    WebshopRoleDataService,
    WebshopService,
    OrderCatalogService,
    OrderCatalogDataService,
    WebshopB2BSettingsDataService
  ]
})
export class WebshopModule { }
